import { Grid, makeStyles } from '@material-ui/core'
import type { Theme } from '@material-ui/core/styles'
import { getObjectLabels } from '@paintscout/util/builder'
import type { StyleClasses } from '../styles'
import React from 'react'
import CheckboxField from '../components/CheckboxField'
import { useClientOptions } from '../providers/ClientOptionsProvider'
import FormSection from '../components/FormSection'
import FormSectionTitle from '../components/FormSectionTitle'
import Typography from '../components/Typography'

const useStyles = makeStyles<Theme, CustomPermissionsFormProps>(
  (_theme) => ({
    root: {}
  }),
  { name: 'CustomPermissionsForm' }
)

export interface CustomPermissionsFormProps {
  classes?: StyleClasses<typeof useStyles>
}

function CustomPermissionsForm(props: CustomPermissionsFormProps) {
  const classes = useStyles(props)
  const { options } = useClientOptions()
  const objectLabels = getObjectLabels({ options })

  return (
    <>
      <FormSectionTitle title={'Custom Permissions'} classes={{ root: classes.title }} variant="h2" />
      <FormSection>
        <FormSectionTitle
          variant="h3"
          title={'Admin'}
          subTitle={`Admin users can always see and edit all ${objectLabels.quote.plural.toLowerCase()}. They can also update all Settings. They can do everything in PaintScout.`}
        />
        <Grid item xs={12}>
          <CheckboxField
            type="checkbox"
            InputProps={{
              checked: true
            }}
            name=""
            label={`Full Access`}
            sublabel={`Admin users always have full access to the complete functionality of PaintScout.`}
          />
        </Grid>
      </FormSection>

      <FormSection>
        <FormSectionTitle
          variant="h3"
          title={'Sales'}
          subTitle={`Sales users can create & edit their own ${objectLabels.quote.plural.toLowerCase()}. They can also update their own profile, but have no access to Account Settings.`}
        />
        <Typography variant="h6" gutterBottom={true}>
          Additional Permissions
        </Typography>
        <Grid item xs={12}>
          <Grid item xs={12}>
            <CheckboxField
              type="checkbox"
              value="read:all-quotes"
              name="options.options.permissions.sales.include"
              label={`View All ${objectLabels.quote.plural}`}
              sublabel={`Let sales users view all ${objectLabels.quote.plural.toLowerCase()} (${objectLabels.quote.plural.toLowerCase()} from other users).`}
            />
          </Grid>
          <Grid item xs={12}>
            <CheckboxField
              type="checkbox"
              value="read:all-invoices"
              name="options.options.permissions.sales.include"
              label={`View All Invoices`}
              sublabel={`Let sales users view all invoices (invoices from other users).`}
            />
          </Grid>
        </Grid>
      </FormSection>

      <FormSection>
        <FormSectionTitle
          variant="h3"
          title={'Crew'}
          subTitle={`Crew users can view and add additional work to existing ${objectLabels.quote.plural.toLowerCase()} & invoices that have been assigned to them.`}
        />
        <Typography variant="h6" gutterBottom={true}>
          Additional Permissions
        </Typography>
        <Grid item xs={12}>
          <Grid item xs={12}>
            <CheckboxField
              type="checkbox"
              value="send:assigned-quotes"
              name="options.options.permissions.crew.include"
              label={`Send Assigned ${objectLabels.quote.plural}`}
              sublabel={`Let crew users send ${objectLabels.quote.plural.toLowerCase()}, in addition to viewing them.`}
            />
          </Grid>
          <Grid item xs={12}>
            <CheckboxField
              type="checkbox"
              value="send:assigned-invoices"
              name="options.options.permissions.crew.include"
              label={`Send Assigned Invoices`}
              sublabel={`Let crew users send Invoices, in addition to viewing them.`}
            />
          </Grid>
          <Grid item xs={12}>
            <CheckboxField
              type="checkbox"
              value="read:all-quotes"
              name="options.options.permissions.crew.include"
              label={`View All ${objectLabels.quote.plural}`}
              sublabel={`Let crew users view all ${objectLabels.quote.plural.toLowerCase()} (${objectLabels.quote.plural.toLowerCase()} from other users).`}
            />
          </Grid>
          <Grid item xs={12}>
            <CheckboxField
              type="checkbox"
              value="read:all-invoices"
              name="options.options.permissions.crew.include"
              label={`View All Invoices`}
              sublabel={`Let crew users view all invoices (not just those that are assigned).`}
            />
          </Grid>
          <Grid item xs={12}>
            <CheckboxField
              type="checkbox"
              value="additional-work:all-quotes"
              name="options.options.permissions.crew.include"
              label={`Add Additional Work to All ${objectLabels.quote.plural}`}
              sublabel={`Let crew users add additional work to all ${objectLabels.quote.plural.toLowerCase()} (even if they haven't been assigned).`}
            />
          </Grid>
        </Grid>
        <Typography variant="h6" gutterBottom={true}>
          Exclude Permissions
        </Typography>
        <Grid item xs={12}>
          <Grid item xs={12}>
            <CheckboxField
              type="checkbox"
              value="read:all-contacts"
              name="options.options.permissions.crew.exclude"
              label={`View No Contacts`}
              sublabel={`Prevent crew users from viewing any contacts.`}
            />
          </Grid>
        </Grid>
        <Grid item xs={12}>
          <Grid item xs={12}>
            <CheckboxField
              type="checkbox"
              value="read:all-companies"
              name="options.options.permissions.crew.exclude"
              label={`View No Companies`}
              sublabel={`Prevent crew users from viewing any companies.`}
            />
          </Grid>
        </Grid>
      </FormSection>

      <FormSection>
        <FormSectionTitle
          variant="h3"
          title={'Office'}
          subTitle={`Office users have send and read-only access to all ${objectLabels.quote.plural.toLowerCase()}.`}
        />
        <Typography variant="h6" gutterBottom={true}>
          Additional Permissions
        </Typography>
        <Grid item xs={12}>
          <Grid item xs={12}>
            <CheckboxField
              type="checkbox"
              value="write-presentation-section:all-quotes"
              name="options.options.permissions.viewer.include"
              label={`Write Presentation Section on all ${objectLabels.quote.plural}`}
              sublabel={`Let office users edit presentations on all ${objectLabels.quote.plural.toLowerCase()}.`}
            />
          </Grid>
        </Grid>
        <Grid item xs={12}>
          <CheckboxField
            type="checkbox"
            value="write-presentation-section:all-quotes"
            name="options.options.permissions.viewer.include"
            label={`Write Presentation Section on all ${objectLabels.quote.plural}`}
            sublabel={`Let office users edit presentations on all ${objectLabels.quote.plural.toLowerCase()}.`}
          />
        </Grid>
        <Typography variant="h6" gutterBottom={true}>
          Exclude Permissions
        </Typography>
        <Grid item xs={12}>
          <Grid item xs={12}>
            <CheckboxField
              type="checkbox"
              value="create:contacts"
              name="options.options.permissions.viewer.exclude"
              label={`No Create Contacts`}
              sublabel={`Prevent office users from creating any contacts.`}
            />
          </Grid>
        </Grid>
        <Grid item xs={12}>
          <Grid item xs={12}>
            <CheckboxField
              type="checkbox"
              value="write:all-contacts"
              name="options.options.permissions.viewer.exclude"
              label={`No Edit Contacts`}
              sublabel={`Prevent office users from updating any contacts.`}
            />
          </Grid>
        </Grid>
      </FormSection>
    </>
  )
}

export default CustomPermissionsForm
